import { Modal } from '@components/Modal'
import ContactForm from '@components/_contact/ContactForm'
import { Heading } from '@components/gassan-ui/Typography/Heading'
import { Text } from '@components/gassan-ui/Typography/Text'
import { Box, Button, KontentImage } from '@gassan-ui'
import { LeadStore } from '@generated'
import { cn } from '@lib/cn'
import * as Elements from '@lib/kontent/models/Elements'
import pinterestTracking from '@lib/pinterest-tracking'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, useState } from 'react'
import { MarginBottomProps, MarginTopProps } from 'styled-system'
import USPs from './USPs'

type FormType = 'default' | 'pre-owned'

type Props = MarginTopProps &
  MarginBottomProps & {
    title?: string
    text?: string
    customImage?: Elements.AssetsElement['value'][0]
    buttonText?: string
    subject?: string
    formType?: FormType
    variant?: 'product-page' | 'default'
    productId?: string
  }

const AppointmentUSPBlock: FC<Props> = ({
  text,
  title,
  buttonText,
  mt,
  mb = ['3.5rem', '4rem', '5rem', '7.5rem'],
  subject,
  productId,
  formType,
  customImage,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation('other')
  const router = useRouter()
  const isProductPage = router.pathname.includes('/p/')

  return (
    <Box
      position="relative"
      pt={{
        _: '3rem',
        large: '3.5rem',
      }}
      pb={mb}
      mt={mt}
    >
      <div className={cn('absolute left-0 top-0 h-[40%] w-full bg-shade-50')} />
      <div
        className={cn(
          'relative mx-auto w-[calc(100%-2rem)] max-w-[90rem] md:w-[calc(100%-3rem)] lg:w-[calc(100%-5.5rem)]',
        )}
      >
        <USPs />
        <div className={cn('bg-pampas')}>
          <div
            id="make-an-appointment"
            className={cn(
              'mx-auto grid w-full max-w-[33rem] grid-cols-1 gap-x-8 lg:mx-0 lg:max-w-none lg:grid-cols-2',
            )}
          >
            <div
              className={cn(
                'flex justify-center px-4 pb-4 pt-10 sm:px-12 sm:pb-20 sm:pt-20 xl:grid-cols-2 xl:px-[6.5rem] xl:pb-[6.5rem] xl:pt-[7.5rem]',
              )}
            >
              <div className={cn('mb-4 w-full text-center lg:mb-0 lg:text-left')}>
                <Heading
                  variant="h2"
                  as="h2"
                  className={cn(
                    'mx-auto mb-4 w-full max-w-[12rem] md:max-w-[18rem] lg:mx-0 lg:max-w-[20rem]',
                  )}
                >
                  {title ? title : t('createAppointmentOrAskQuestion')}
                </Heading>

                <Text className={cn('mx-auto mb-6 w-full max-w-[26rem] lg:mx-0 lg:mb-6')}>
                  {text ? text : t('createAppointmentOrAskQuestionDescription')}
                </Text>

                <Button variant="dark" data-open-contact-modal onClick={() => setIsOpen(true)}>
                  {buttonText ? buttonText : t('createAppointmentOrAskQuestionButton')}
                </Button>
                <Modal
                  open={isOpen}
                  onOpenChange={(open) => setIsOpen(open)}
                  title={t('contactUs')}
                >
                  <ContactForm
                    subject={subject}
                    leadStore={formType === 'pre-owned' ? LeadStore.PreOwned : LeadStore.Gassan}
                    isProductPage={isProductPage}
                    onSubmitSuccess={(values) => {
                      if (productId) {
                        pinterestTracking.lead({
                          lead_type:
                            values.type === 'APPOINTMENT'
                              ? 'product-appointment'
                              : 'product-question',
                          product_id: productId,
                        })
                      }
                      setIsOpen(false)
                    }}
                  />
                </Modal>
              </div>
            </div>
            {customImage ? (
              <KontentImage
                className="h-full w-full object-cover"
                src={customImage.url}
                alt={customImage.description}
              />
            ) : (
              <img
                className="h-full w-full object-cover"
                src="/images/contact-form-featured-image.png"
                alt="Contact us"
              />
            )}
          </div>
        </div>
      </div>
    </Box>
  )
}

export default AppointmentUSPBlock
